import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
} from 'recharts';
import ChartCard from './ChartCard';
import ChartTooltip from './ChartTooltip';
import MarketDataFootnote from './MarketDataFootnote';
import {
  EV_SEGMENT,
  BAR_MAX_SIZE,
  gridProps,
  xAxisProps,
  yAxisProps,
  legendProps,
  zeroLineProps,
  CHART_TYPE,
} from './chartTheme';
import { formatFiscalYear } from '../utils/formatters';
import type { UnifiedFinancialRecord, RegionConfig } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
}

/**
 * Enterprise Value Breakdown — shown as % contribution to total EV.
 *
 * Segments (all as % of total EV, so they reconcile to 100%):
 *   - EV from EBIT (current operations)
 *   - EV from Future Growth
 *   - EV from Market Turnaround  (residual = EV − EBIT − Growth)
 *
 * The backend caps the EBIT leg with a negative EV_from_EBIT_Inflated offset
 * whenever EV_from_EBIT > total EV (and zeroes growth/market/CAP). We fold that
 * offset back into the EBIT segment rather than drawing it as its own bar, so
 * a capped year reads as "all of EV is current operations" (100/0/0). Those
 * years are flagged in a footnote instead.
 */
export default function EVBreakdownChart({ data, region }: Props) {
  const chartData = data
    .filter(
      (d) =>
        (d.enterprise_value ?? 0) !== 0 &&
        (d.ev_from_ebit != null ||
          d.ev_from_future_growth != null ||
          d.ev_from_ebit_inflated != null)
    )
    .map((d) => {
      const total = d.enterprise_value as number;
      // Backend caps the EBIT leg with a negative EV_from_EBIT_Inflated offset when
      // EV_from_EBIT > EV, zeroing growth/market/CAP — so `ebit + inflated === total`
      // in that regime. Folding it in states what the decomposition means ("all of
      // EV is current operations") instead of drawing an overshoot the reader nets
      // out by eye. Normal regime: inflated === 0, so this is a no-op.
      const ebit = (d.ev_from_ebit ?? 0) + (d.ev_from_ebit_inflated ?? 0);
      const growth = d.ev_from_future_growth ?? 0;
      // Market turnaround is the residual, so the parts reconcile to EV.
      const market = total - ebit - growth;
      const pct = (v: number) => parseFloat(((v / total) * 100).toFixed(1));
      return {
        year: formatFiscalYear(d.year, region),
        'EV from EBIT %': pct(ebit),
        'EV from Growth %': pct(growth),
        'EV from Market %': pct(market),
      };
    });

  // Only render Market when it actually contributes, so the legend stays clean
  // for the common EBIT-vs-Growth case.
  const hasMarket = chartData.some((d) => Math.abs(d['EV from Market %']) >= 0.5);

  // Years the backend capped. Deliberately a strict !== 0 test, unlike hasMarket's
  // 0.5% draw threshold — this asks a factual yes/no, not "is the segment visible".
  const cappedYears = data
    .filter((d) => (d.ev_from_ebit_inflated ?? 0) !== 0)
    .map((d) => formatFiscalYear(d.year, region));

  if (chartData.length === 0) {
    return (
      <ChartCard
        title="Enterprise Value Breakdown: Current vs Future Growth (%)"
        empty="EV decomposition data not available"
      />
    );
  }

  // Capped-years line, folded into the shared market footnote as `extra` so a
  // pre-listing company gets ONE consistent note block: the leading-gap sentence
  // (EV breakdown needs a market-priced EV) plus this cap explanation.
  const cappedNote =
    cappedYears.length > 0 ? (
      <p className="text-[11px] text-amber-700 flex items-start gap-1">
        <span aria-hidden="true">⚠</span>
        <span>
          {cappedYears.join(', ')}: EV from EBIT exceeded total EV. The excess is
          capped, so current operations account for the whole of EV and future
          growth contributes nothing.
        </span>
      </p>
    ) : undefined;

  return (
    <ChartCard
      title="Enterprise Value Breakdown: Current vs Future Growth (%)"
      subtitle="Each segment as a share of total EV"
      footnote={
        <MarketDataFootnote
          records={data}
          region={region}
          // This chart filters unpriced years out of the bars entirely, so there
          // are no hoverable empty columns — suppress the "hover a year" wording.
          hoverable={false}
          hasMetric={(r) => (r.enterprise_value ?? 0) !== 0}
          metricLabel="the EV breakdown"
          extra={cappedNote}
        />
      }
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={chartData} margin={{ top: 16, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" {...xAxisProps} />
          <YAxis {...yAxisProps} domain={['auto', 'auto']} tickFormatter={(v) => `${v.toFixed(0)}%`} />
          <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(1)}%`} />} />
          <Legend {...legendProps} />
          <ReferenceLine y={0} {...zeroLineProps} />
          {/* maxBarSize caps the width so a single year of data renders as a
              centered, intentional-looking stack instead of one full-bleed bar. */}
          <Bar dataKey="EV from EBIT %" name="EV from EBIT" stackId="ev" fill={EV_SEGMENT.ebit} maxBarSize={BAR_MAX_SIZE}>
            {/* Only the EBIT segment carries an in-bar label: at EV_SEGMENT.ebit it
                clears 4.5:1 for white text. Growth/Market read off the y-axis and
                tooltip, where their lighter fills would fail contrast. */}
            <LabelList
              dataKey="EV from EBIT %"
              position="inside"
              formatter={(v: number) => (Math.abs(v) >= 8 ? `${v.toFixed(0)}%` : '')}
              style={{ fontSize: CHART_TYPE.dataLabel, fill: '#fff', fontWeight: 600 }}
            />
          </Bar>
          <Bar dataKey="EV from Growth %" name="EV from Growth" stackId="ev" fill={EV_SEGMENT.growth} maxBarSize={BAR_MAX_SIZE} />
          {hasMarket && (
            <Bar
              dataKey="EV from Market %"
              name="EV from Market Turnaround"
              stackId="ev"
              fill={EV_SEGMENT.market}
              maxBarSize={BAR_MAX_SIZE}
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
